import React, { useCallback, useMemo } from "react";
import { SelectorDisplayItemProps } from "../components/Selector";
import { Fetcher } from "../components/Fetcher";
import { FilterContainer } from "../components/FilterContainer";
import { DEFAULT_PAGING_SIZE, MAX_TABLE_HEIGHT, MIN_TABLE_HEIGHT } from "../constants";
import { Pagination } from "../components/Pagination";
import { makeStyles } from "@material-ui/core/styles";
import { grey } from "@material-ui/core/colors";
import { MessageCard } from "../components/MessageCard";
import { Typography } from "@material-ui/core";
import { getTwilioWorkspaces, getTwilioWorkers, getTwilioWorkerChannels } from "../services/api/twilioWorkerLogs.api";
import { store } from "../globalState/StateProvider";
import {
	setWorkerLogsFiltersLocalStorageValue,
	getWorkerLogsFiltersLocalStorageValue,
} from "../globalState/LocalStorage/TwilioLocalStorageAccessors";
import { WorkerLogsFilterObject } from "../models/TwilioModels";
import { WorkerResponse, WorkspaceResponse } from "../models/TwilioModels";
import { TwilioWorkerListContainer } from "../components/TwilioWorkerListContainer";

interface WorkerLogsPageProps {
	// subscription id to fetch details
	subscriptionId: string;
	// product id used to fetch the subscription
	productId: string;
}

const useStyles = makeStyles((theme) => ({
	title: {
		marginBottom: theme.spacing(3),
	},
	tableWrapper: {
		minHeight: MIN_TABLE_HEIGHT,
		maxHeight: MAX_TABLE_HEIGHT,
		maxWidth: 1200,
		overflow: "auto",
		borderBottomColor: grey[100],
		borderBottomWidth: 1,
		borderBottomStyle: "solid",
	},
	tableTitle: {
		zIndex: 1,
	},
}));

const renderList = (
	workers: WorkerResponse[],
	classes: Record<any, string>,
	setPageNumber: (n: number) => void,
	setRowsPerPage: (n: number) => void,
	pageNumber: number,
	rowsPerPage: number,
	subscriptionId: string,
	accountSid: string,
	productId: string,
	workspaceSid: string,
) => {
	function loadWorkerChannels(workerSid: string) {
		return getTwilioWorkerChannels(productId, subscriptionId, accountSid, workspaceSid, workerSid);
	}

	return (
		<div>
			{workers.length > 0 ? (
				<div>
					<div className={classes.tableWrapper}>
						<TwilioWorkerListContainer
							workers={workers}
							subscriptionId={subscriptionId}
							accountSid={accountSid}
							productId={productId}
							loadWorkerChannels={loadWorkerChannels}
						></TwilioWorkerListContainer>
					</div>
					<Pagination
						pageNumber={pageNumber}
						rowsPerPage={rowsPerPage}
						setPageFunction={setPageNumber}
						setRowsPerPageFunction={setRowsPerPage}
						currentPageLength={workers.length}
					></Pagination>
				</div>
			) : (
				<MessageCard message="No workers found." />
			)}
		</div>
	);
};

/**
 * This page is used to show the list of logs for individual workers.
 * @param props
 */
export const WorkerLogsPage = (props: WorkerLogsPageProps) => {
	const classes = useStyles();
	const { environments, subscription } = React.useContext(store);

	const environmentsList = useMemo(
		() => (subscription && subscription!.subscription!.subscriptionId === props.subscriptionId ? environments : []),
		[environments, props.subscriptionId, subscription],
	);

	const selectableAvailabilities: SelectorDisplayItemProps[] = [
		{ displayString: "Any", valueString: "any" },
		{ displayString: "Available", valueString: "true" },
		{ displayString: "Unavailable", valueString: "false" },
	];

	const [pageNumber, setPageNumber] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_PAGING_SIZE);
	const filterObject: WorkerLogsFilterObject = getWorkerLogsFiltersLocalStorageValue(props.subscriptionId);
	const [initialWorkspace, setInitialWorkspaceSid] = React.useState("");
	const [workspaces, setWorkspaces] = React.useState<SelectorDisplayItemProps[]>();
	const [update, forceUpdate] = React.useState(false);
	const [isLoaded, setIsLoaded] = React.useState(false);

	function UpdateFilters(newFilterObject) {
		setWorkerLogsFiltersLocalStorageValue(props.subscriptionId, newFilterObject);
		setPageNumber(0);
		forceUpdate(!update);
	}

	const getWorkerLogsRequest = {
		page: pageNumber,
		pageSize: rowsPerPage,
		workspaceSid: filterObject.selectedWorkspace === "" ? initialWorkspace : filterObject.selectedWorkspace,
		searchFriendlyName: filterObject.searchFriendlyName,
		available: filterObject.selectedAvailability,
	};

	function loadList() {
		if (isLoaded && filterObject.selectedWorkspace !== "") {
			return (
				<div>
					<Fetcher
						action={getTwilioWorkers(
							getWorkerLogsRequest,
							props.subscriptionId,
							props.productId,
							filterObject.selectedEnvironment !== ""
								? filterObject.selectedEnvironment
								: environmentsList[0].valueString!,
						)}
					>
						{(response) =>
							renderList(
								response.data,
								classes,
								setPageNumber,
								setRowsPerPage,
								pageNumber,
								rowsPerPage,
								props.subscriptionId,
								filterObject.selectedEnvironment !== ""
									? filterObject.selectedEnvironment
									: environmentsList[0].valueString!,
								props.productId,
								filterObject.selectedWorkspace,
							)
						}
					</Fetcher>
				</div>
			);
		}
	}

	const fetchWorkspacesList = useCallback(
		async function (environment: string) {
			const response = await getTwilioWorkspaces(props.productId, props.subscriptionId, environment);
			const workspaces: WorkspaceResponse[] = response ? response.data : [];
			const newWorkspaces: SelectorDisplayItemProps[] = [];

			newWorkspaces.push(
				...workspaces.map((item) => {
					return {
						displayString: item.friendlyName,
						valueString: item.sid,
					};
				}),
			);

			return newWorkspaces;
		},
		[props.productId, props.subscriptionId],
	);

	React.useEffect(() => {
		const fetch = async () => {
			if (environmentsList.length > 0) {
				const workspaceList = await fetchWorkspacesList(
					filterObject.selectedEnvironment !== "" ? filterObject.selectedEnvironment : environmentsList[0].valueString!,
				);
				setWorkspaces(workspaceList);
				setInitialWorkspaceSid(workspaceList.length > 0 ? workspaceList[0].valueString! : "");
				setIsLoaded(true);
			}
		};
		fetch();
	}, [environments, environmentsList, fetchWorkspacesList, filterObject.selectedEnvironment]);

	function showFilters() {
		if (environmentsList.length > 0 && workspaces) {
			return (
				<FilterContainer
					environmentFilter={{
						environments: environmentsList,
						selectedEnvironment:
							filterObject.selectedEnvironment != ""
								? filterObject.selectedEnvironment
								: environmentsList[0].valueString!,
					}}
					fetchAccountWorkspaces={fetchWorkspacesList}
					environmentWorkspaces={workspaces}
					selectedWorkspace={
						filterObject.selectedWorkspace != "" ? filterObject.selectedWorkspace : workspaces[0].valueString!
					}
					searchFriendlyName={filterObject.searchFriendlyName}
					availabilityFilter={{
						selectableAvailabilities: selectableAvailabilities,
						selectedAvailability: filterObject.selectedAvailability,
					}}
					onUpdate={UpdateFilters}
				></FilterContainer>
			);
		}
	}

	return (
		<div data-testid={"subscription-twilio-channel-list-page"}>
			<Typography className={classes.title} variant="h4">
				Worker Logs
			</Typography>
			{showFilters()}
			{loadList()}
		</div>
	);
};
